import { motion } from "framer-motion";
import { Users, Settings, HardDrive, Shield, Package, HeadphonesIcon } from "lucide-react";

const stappen = [
  {
    icon: Users,
    title: "Kennismaking",
    subtitle: "Wat heb je nu?",
    text: "We drinken een kop koffie en brengen samen in kaart hoe jullie nu werken. Welke systemen, mailadressen, opslag en apparaten er zijn. Zo weten we precies wat er moet verhuizen — en wat misschien eindelijk weg mag.",
    gradient: "bg-hero-gradient",
  },
  {
    icon: Settings,
    title: "Inrichting",
    subtitle: "Wij bouwen je nieuwe omgeving",
    text: "We richten je Microsoft 365-omgeving helemaal in: accounts, beveiliging, e-mail, OneDrive, SharePoint en Teams. Alles staat klaar vóórdat er ook maar iets wordt omgezet.",
    gradient: "bg-rinqel-blue-gradient",
  },
  {
    icon: HardDrive,
    title: "Datamigratie",
    subtitle: "Alles wordt verhuisd",
    text: "E-mail, agenda's, contacten en bestanden? Die verhuizen we zorgvuldig naar de nieuwe omgeving. Niets gaat verloren — we controleren alles dubbel. Jij merkt er bijna niets van.",
    gradient: "bg-rinqel-green-gradient",
  },
  {
    icon: Shield,
    title: "Beveiliging",
    subtitle: "Dicht als een kluis",
    text: "Tweestapsverificatie, voorwaardelijke toegang en beveiligingsbeleid worden ingesteld volgens de laatste standaarden. Je omgeving is vanaf dag één stevig op slot.",
    gradient: "bg-hero-gradient",
  },
  {
    icon: Package,
    title: "Apparaten klaarmaken",
    subtitle: "Inloggen en klaar",
    text: "Laptops, desktops en telefoons worden automatisch ingericht met alle apps en instellingen. Schoon geïnstalleerd, als nieuw. Je hoeft alleen maar in te loggen.",
    gradient: "bg-rinqel-blue-gradient",
  },
  {
    icon: HeadphonesIcon,
    title: "Go-live!",
    subtitle: "Wij staan naast je",
    text: "Op de grote dag staan we klaar. We begeleiden je team, beantwoorden vragen en zorgen dat iedereen lekker op gang komt. En daarna? Dan zijn we er gewoon nog steeds.",
    gradient: "bg-rinqel-green-gradient",
  },
];

const MigrationTimeline = () => {
  return (
    <section className="py-16 md:py-24">
      <div className="container">
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl font-bold md:text-3xl">
            De overstap in{" "}
            <span className="text-gradient">6 stappen</span>
          </h2>
          <p className="mt-3 text-muted-foreground text-lg">
            Geen verrassingen, geen gedoe. Gewoon een helder plan van A tot Z.
          </p>
        </motion.div>

        {/* Visual timeline */}
        <div className="relative mx-auto max-w-4xl">
          {/* Vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-border md:left-1/2 md:-translate-x-px" />

          {stappen.map((stap, i) => {
            const Icon = stap.icon;
            const isLeft = i % 2 === 0;

            return (
              <motion.div
                key={stap.title}
                className={`relative mb-8 last:mb-0 flex items-start gap-4 md:gap-0 ${
                  isLeft ? "md:flex-row" : "md:flex-row-reverse"
                }`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
              >
                {/* Connector dot (mobile: left, desktop: center) */}
                <div className="absolute left-6 top-3 z-10 md:left-1/2 md:-translate-x-1/2">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-full ${stap.gradient} text-primary-foreground shadow-lg ring-4 ring-background`}>
                    <span className="text-sm font-bold">{i + 1}</span>
                  </div>
                </div>

                {/* Content card */}
                <div className={`ml-20 md:ml-0 md:w-[calc(50%-40px)] ${isLeft ? "md:pr-0 md:text-right" : "md:pl-0 md:text-left"} ${isLeft ? "md:mr-auto" : "md:ml-auto"}`}>
                  <div className="rounded-xl border border-border bg-card p-5 shadow-card hover:shadow-card-hover transition-shadow">
                    <div className={`flex items-center gap-3 ${isLeft ? "md:flex-row-reverse" : ""}`}>
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${stap.gradient} text-primary-foreground`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-base">{stap.title}</h3>
                        <p className="text-xs text-muted-foreground">{stap.subtitle}</p>
                      </div>
                    </div>
                    <p className={`mt-3 text-sm leading-relaxed text-muted-foreground ${isLeft ? "md:text-right" : "md:text-left"}`}>
                      {stap.text}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MigrationTimeline;
